import { AircraftSchema } from '@/features/end-of-lease/types';
import {
    Query,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import type { PaginatedResponse } from '@/lib/api';
import { toCamelCase, toSnakeCase } from '@/lib/case';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import type { DBLineItem, LineItemSort } from './types';

export const useAircrafts = (search?: string) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['aircrafts', search],
        queryFn: async () => {
            const response = await client.get('/api/leasing/aircrafts', {
                params: search ? { search } : undefined,
            });
            return toCamelCase<AircraftSchema[]>(response.data);
        },
        enabled,
    });
};

export const useAircraft = (id: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['aircraft', id],
        queryFn: async () => {
            const response = await client.get(`/api/leasing/aircrafts/${id}`);
            return toCamelCase<AircraftSchema>(response.data);
        },
        enabled: enabled && !!id,
        retry: false,
    });
};

export const useCreateAircraft = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            aircraft,
            createProject = false,
        }: {
            aircraft: Omit<
                AircraftSchema,
                'id' | 'teamId' | 'createdAt' | 'updatedAt' | 'projects'
            >;
            createProject?: boolean;
        }) => {
            const response = await client.post(
                `/api/leasing/aircrafts`,
                toSnakeCase({ ...aircraft, createProject }),
            );
            return toCamelCase<AircraftSchema>(response.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['aircrafts'] });
        },
    });
};

export const useAircraftDocumentsCount = (aircraftId: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['aircrafts', aircraftId, 'documents', 'count'],
        queryFn: async () => {
            const response = await client.get(
                `/api/leasing/aircrafts/${aircraftId}/documents/count`,
            );
            return toCamelCase<{ count: number }>(response.data);
        },
        enabled: enabled && !!aircraftId,
    });
};

export const useDeleteAircraft = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (aircraftId: number) => {
            await client.delete(`/api/leasing/aircrafts/${aircraftId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['aircrafts'] });
        },
    });
};

interface UseLineItemsParams {
    docId?: number;
    limit?: number;
    skip?: number;
    search?: string;
    status?: string;
    chapterNo?: string;
    aiFindings?: boolean;
    completionStatus?: boolean;
    /** Sorted before paging, server-side; omitted keeps the chapter/item order. */
    sort?: LineItemSort;
    isEnabled?: boolean;
}

export const useLineItems = ({
    docId,
    limit = 10,
    skip = 0,
    search,
    status,
    chapterNo,
    aiFindings,
    completionStatus,
    sort,
    isEnabled = true,
}: UseLineItemsParams) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: [
            'lineItems',
            {
                docId,
                limit,
                skip,
                search,
                status,
                chapterNo,
                aiFindings,
                completionStatus,
                sort,
            },
        ],
        queryFn: async () => {
            const params = {
                limit,
                skip,
                ...(docId && { doc_id: docId }),
                ...(search && { search }),
                ...(status && { status }),
                ...(chapterNo && { chapter_no: chapterNo }),
                ...(aiFindings !== undefined && { ai_findings: aiFindings }),
                ...(completionStatus !== undefined && {
                    completion_status: completionStatus,
                }),
                ...(sort && { sort }),
            };

            const response = await client.get('/api/line_items/', { params });
            return toCamelCase<PaginatedResponse<DBLineItem>>(response.data);
        },
        enabled: enabled && isEnabled,
    });
};

export const useLineItem = (
    lineItemId: number,
    isEnabled = true,
    options?: {
        refetchInterval?:
            | number
            | false
            | ((query: Query<DBLineItem>) => number | false | undefined);
    },
) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['lineItems', lineItemId],
        queryFn: async () => {
            const response = await client.get(`/api/line_items/${lineItemId}`);
            return toCamelCase<DBLineItem>(response.data);
        },
        enabled: enabled && isEnabled && !!lineItemId,
        refetchInterval: options?.refetchInterval,
    });
};

interface UpdateLineItemPayload {
    mroDfpRefs?: string[];
    mroNrcRefs?: string[];
    taskNo?: string;
    itemNo?: string;
}

export const useUpdateLineItem = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            lineItemId,
            payload,
        }: {
            lineItemId: number;
            payload: UpdateLineItemPayload;
        }) => {
            const response = await client.patch(
                `/api/line_items/${lineItemId}`,
                toSnakeCase(payload),
            );
            return toCamelCase<DBLineItem>(response.data);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['lineItems', variables.lineItemId],
            });
        },
    });
};

type WorkOrderJobCardStats = {
    totalJobCards: number;
    failedComplianceCount: number;
};

type WorkOrderLineItemProgressStats = {
    totalLineItems: number;
    completedLineItems: number;
    progressPercentage: number;
};

export const useWorkOrderJobCardStats = (
    docId: number,
    projectId: number,
    isEnabled = true,
) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['workOrderJobCardStats', docId, projectId],
        queryFn: async () => {
            const response = await client.get(
                `/api/leasing/documents/${docId}/job_card_stats`,
                {
                    params: { project_id: projectId },
                },
            );
            return toCamelCase<WorkOrderJobCardStats>(response.data);
        },
        enabled: enabled && isEnabled && !!docId && !!projectId,
    });
};

export const useWorkOrderDRStats = (
    docId: number,
    projectId: number,
    isEnabled = true,
) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['workOrderDRStats', docId, projectId],
        queryFn: async () => {
            const response = await client.get(
                `/api/leasing/documents/${docId}/dr_stats`,
                {
                    params: { project_id: projectId },
                },
            );
            return toCamelCase<WorkOrderJobCardStats>(response.data);
        },
        enabled: enabled && isEnabled && !!docId && !!projectId,
    });
};

export const useWorkOrderComponentActivityStats = (
    docId: number,
    projectId: number,
    isEnabled = true,
) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['workOrderComponentActivityStats', docId, projectId],
        queryFn: async () => {
            const response = await client.get(
                `/api/leasing/documents/${docId}/component_activity_stats`,
                {
                    params: { project_id: projectId },
                },
            );
            return toCamelCase<WorkOrderJobCardStats>(response.data);
        },
        enabled: enabled && isEnabled && !!docId && !!projectId,
    });
};

export const useWorkOrderLineItemProgress = (docId: number, isEnabled = true) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['workOrderLineItemProgress', docId],
        queryFn: async () => {
            const response = await client.get(
                `/api/leasing/documents/${docId}/line_item_progress`,
            );
            return toCamelCase<WorkOrderLineItemProgressStats>(response.data);
        },
        enabled: enabled && isEnabled && !!docId,
    });
};
